import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, Eye, Copy, CircleCheck as CheckCircle2, FileText, Download, X,
  TriangleAlert as AlertTriangle, CreditCard as Edit3, Save, MapPin,
  Square, SquareCheck as CheckSquare, DollarSign, Phone, Trash2, ShieldBan
} from 'lucide-react';
import ClientDetailModal from '../components/ClientDetailModal';
import CircuitRouteModal from '../components/CircuitRouteModal';

interface OverdueClient {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  relative_phones: { numero: string; relacao: string }[];
  email: string;
  address: string;
  business_address: string;
  pix_key: string;
  ruta: number;
  total_debt: number;
  overdue_days: number;
  observation?: string;
  seller: {
    email: string;
  } | null;
  contract_files: {
    id: string;
    file_name: string;
    file_url: string;
    file_type: string;
  }[];
  contracts: {
    id: string;
    loan_amount: number;
    total_amount: number;
    created_at: string;
    installments: {
      id: string;
      due_date: string;
      amount: number;
      status: string;
      paid_amount?: number;
      remaining_amount: number;
      days_overdue: number;
      is_overdue: boolean;
    }[];
  }[];
}

interface RutaSummary {
  ruta: number;
  total_clients: number;
  total_debt: number;
  clients: OverdueClient[];
}

export default function Curvas() {
  const { user } = useAuth();
  const [rutaSummaries, setRutaSummaries] = useState<RutaSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<OverdueClient | null>(null);
  const [copied, setCopied] = useState(false);
  const [selectedRuta, setSelectedRuta] = useState<number>(0);
  const [editingObservation, setEditingObservation] = useState<string | null>(null);
  const [observationText, setObservationText] = useState('');
  const [clientDetailContractId, setClientDetailContractId] = useState<string | null>(null);
  const [circuitSelected, setCircuitSelected] = useState<Set<string>>(new Set());
  const [showCircuitModal, setShowCircuitModal] = useState(false);
  const [circuitStopsForModal, setCircuitStopsForModal] = useState<any[]>([]);
  const [existingCircuitStops, setExistingCircuitStops] = useState<Set<string>>(new Set());
  const [deleteSelected, setDeleteSelected] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);

  const canManageBlacklist = user?.role === 'manager' || user?.role === 'admin';

  useEffect(() => {
    fetchCurvasClients();
  }, [user]);

  useEffect(() => {
    if (user?.role === 'manager') fetchExistingCircuitStops();
  }, [user]);

  const fetchExistingCircuitStops = async () => {
    if (!user?.unit_id) return;
    try {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const startDate = `${year}-${month}-01`;
      const lastDay = new Date(year, today.getMonth() + 1, 0).getDate();
      const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

      const { data } = await supabase
        .from('circuit_routes')
        .select('circuit_route_stops(installment_id)')
        .eq('unit_id', user.unit_id)
        .gte('route_date', startDate)
        .lte('route_date', endDate);

      const stopIds = new Set<string>();
      data?.forEach((route: any) => {
        (route.circuit_route_stops || []).forEach((s: any) => stopIds.add(s.installment_id));
      });
      setExistingCircuitStops(stopIds);
    } catch (err) {
      console.error('Error fetching existing circuit stops:', err);
    }
  };

  const toggleCircuitSelect = (clientId: string) => {
    setCircuitSelected(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const toggleDeleteSelect = (clientId: string) => {
    setDeleteSelected(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const allFilteredClientIds = () => {
    const ids: string[] = [];
    getFilteredSummaries().forEach(summary => {
      summary.clients.forEach(c => ids.push(c.id));
    });
    return ids;
  };

  const toggleSelectAllDelete = () => {
    const ids = allFilteredClientIds();
    const allSelected = ids.length > 0 && ids.every(id => deleteSelected.has(id));
    setDeleteSelected(allSelected ? new Set() : new Set(ids));
  };

  const getSelectedClients = (): OverdueClient[] => {
    const result: OverdueClient[] = [];
    rutaSummaries.forEach(summary => {
      summary.clients.forEach(c => {
        if (deleteSelected.has(c.id)) result.push(c);
      });
    });
    return result;
  };

  const deleteContractCascade = async (contractId: string) => {
    const { error: lateFeeError } = await supabase
      .from('late_fee_allocations')
      .delete()
      .or(`source_contract_id.eq.${contractId},target_contract_id.eq.${contractId}`);
    if (lateFeeError) throw lateFeeError;

    const { error: circuitStopsError } = await supabase
      .from('circuit_route_stops')
      .delete()
      .eq('contract_id', contractId);
    if (circuitStopsError) throw circuitStopsError;

    const { error: editRequestsError } = await supabase
      .from('client_edit_requests')
      .delete()
      .eq('contract_id', contractId);
    if (editRequestsError) throw editRequestsError;

    const { error: selfRegError } = await supabase
      .from('client_self_registrations')
      .delete()
      .eq('contract_id', contractId);
    if (selfRegError) throw selfRegError;

    const { error: modRequestsError } = await supabase
      .from('contract_modification_requests')
      .delete()
      .eq('contract_id', contractId);
    if (modRequestsError) throw modRequestsError;

    const { error: installmentsError } = await supabase
      .from('installments')
      .delete()
      .eq('contract_id', contractId);
    if (installmentsError) throw installmentsError;

    const { error: filesError } = await supabase
      .from('contract_files')
      .delete()
      .eq('contract_id', contractId);
    if (filesError) throw filesError;

    const { error: contractError } = await supabase
      .from('contracts')
      .delete()
      .eq('id', contractId);
    if (contractError) throw contractError;
  };

  const handleDeleteToBlacklist = async () => {
    const clients = getSelectedClients();
    if (clients.length === 0 || !user) return;

    setDeleting(true);
    setError(null);
    try {
      for (const client of clients) {
        for (const contract of client.contracts) {
          await deleteContractCascade(contract.id);
        }

        const cpfDigits = client.cpf.replace(/\D/g, '');
        const { error: blacklistError } = await supabase
          .from('cpf_blacklist')
          .upsert({
            cpf: cpfDigits,
            client_name: client.client_name,
            reason: 'Curva',
            deleted_from_unit_id: user.unit_id || null,
            deleted_by_user_id: user.id || null,
          }, { onConflict: 'cpf' });
        if (blacklistError) throw blacklistError;
      }

      const deletedIds = new Set(clients.map(c => c.id));
      setRutaSummaries(prev =>
        prev
          .map(summary => {
            const remaining = summary.clients.filter(c => !deletedIds.has(c.id));
            return {
              ...summary,
              clients: remaining,
              total_clients: remaining.length,
              total_debt: remaining.reduce((sum, c) => sum + c.total_debt, 0),
            };
          })
          .filter(summary => summary.clients.length > 0)
      );

      setDeleteSelected(new Set());
      setShowDeleteConfirm(false);
      setDeleteSuccess(
        `${clients.length} cliente${clients.length !== 1 ? 's' : ''} exclu${clients.length !== 1 ? 'ídos' : 'ído'} e enviado${clients.length !== 1 ? 's' : ''} para a blacklist como Curva.`
      );
      setTimeout(() => setDeleteSuccess(null), 5000);
    } catch (err) {
      console.error('Erro ao excluir clientes em curva:', err);
      const message = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(`Erro ao excluir e enviar para blacklist: ${message}`);
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const getCircuitStopsData = () => {
    const stopsData: { installmentId: string; contractId: string; clientName: string; address: string; phone: string; amount: number; ruta: number; date: string; clientDataNotes?: string }[] = [];
    const filtered = getFilteredSummaries();
    filtered.forEach(summary => {
      summary.clients.forEach(client => {
        if (circuitSelected.has(client.id)) {
          stopsData.push({
            installmentId: client.contracts[0]?.installments[0]?.id || client.id,
            contractId: client.id,
            clientName: client.client_name,
            address: client.address || '',
            phone: client.phone || '',
            amount: client.total_debt,
            ruta: client.ruta,
            date: new Date().toISOString().split('T')[0],
          });
        }
      });
    });
    return stopsData;
  };

  const handleOpenCircuitModal = async () => {
    const stopsData = getCircuitStopsData();
    if (stopsData.length === 0) return;

    const contractIds = [...new Set(stopsData.map(s => s.contractId))];
    const { data: contracts } = await supabase
      .from('contracts')
      .select(`
        id,
        client_name,
        cpf,
        phone,
        relative_phones,
        email,
        address,
        business_address,
        pix_key,
        code,
        loan_amount,
        total_amount,
        created_at,
        ruta,
        installments(id, due_date, amount, paid_amount, status)
      `)
      .in('id', contractIds);

    const contractMap = new Map<string, any>();
    contracts?.forEach((c: any) => contractMap.set(c.id, c));

    stopsData.forEach(s => {
      const contract = contractMap.get(s.contractId);
      if (!contract) return;
      s.address = contract.address || '';

      const balance = (contract.installments || []).reduce((total: number, inst: any) => {
        return total + (inst.amount - (inst.paid_amount || 0));
      }, 0);

      const relPhones = (contract.relative_phones || [])
        .filter((p: any) => p.numero?.trim())
        .map((p: any) => `${p.relacao}: ${p.numero}`)
        .join('\n');

      const parcelas = (contract.installments || [])
        .map((inst: any, idx: number) =>
          `${idx + 1}a Parcela: ${formatCurrency(inst.amount)} - Venc: ${new Date(inst.due_date + 'T00:00:00Z').toLocaleDateString('pt-BR')}`
        )
        .join('\n');

      s.clientDataNotes = `Dados do Cliente:
Nome: ${contract.client_name}
CPF: ${contract.cpf || ''}
Telefone: ${contract.phone || ''}
${relPhones ? `Telefones Parentes:\n${relPhones}` : ''}
Endereco Residencial: ${contract.address || ''}
Endereco Comercial: ${contract.business_address || ''}
Valor Emprestimo: ${formatCurrency(contract.loan_amount)}
Valor Total: ${formatCurrency(contract.total_amount)}
Saldo Devedor: ${balance <= 0 ? 'Quitado' : formatCurrency(balance)}
Ruta: ${contract.ruta}
${parcelas ? `\nParcelas:\n${parcelas}` : ''}`.trim();
    });

    setCircuitStopsForModal(stopsData);
    setShowCircuitModal(true);
  };

  const circuitRouteDate = useMemo(() => {
    return new Date().toISOString().split('T')[0];
  }, []);

  const fetchCurvasClients = async () => {
    if (!user) return;

    try {
      const { data: installments, error: installmentsError } = await supabase
        .from('installments')
        .select(`
          id,
          due_date,
          amount,
          status,
          paid_amount,
          contract:contracts(
            id,
            client_name,
            cpf,
            phone,
            relative_phones,
            email,
            address,
            business_address,
            pix_key,
            loan_amount,
            total_amount,
            created_at,
            ruta,
            approval_status,
            payment_status,
            seller:seller_id(email),
            contract_files(*)
          )
        `)
        .eq('unit_id', user?.unit_id)
        .neq('status', 'paid');

      if (installmentsError) throw installmentsError;

      const { data: observations, error: observationsError } = await supabase
        .from('client_observations')
        .select('*')
        .eq('unit_id', user?.unit_id);

      if (observationsError) throw observationsError;

      const observationsMap = new Map();
      observations?.forEach(obs => {
        observationsMap.set(obs.client_id, obs.observation_text);
      });

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const overdueClients: { [key: string]: OverdueClient } = {};

      installments?.forEach(installment => {
        if (installment.contract.approval_status !== 'approved') return;

        const dueDate = new Date(installment.due_date);
        dueDate.setHours(23, 59, 59, 999);

        if (dueDate >= today) return;

        const overdueDays = Math.ceil((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));

        // Curvas: apenas clientes com mais de 90 dias de atraso
        if (overdueDays <= 90) return;

        const remainingAmount = installment.amount - (installment.paid_amount || 0);

        if (remainingAmount > 0) {
          const clientKey = installment.contract.cpf;

          if (!overdueClients[clientKey]) {
            overdueClients[clientKey] = {
              id: installment.contract.id,
              client_name: installment.contract.client_name,
              cpf: installment.contract.cpf,
              phone: installment.contract.phone,
              relative_phones: installment.contract.relative_phones,
              email: installment.contract.email,
              address: installment.contract.address,
              business_address: installment.contract.business_address,
              pix_key: installment.contract.pix_key,
              ruta: installment.contract.ruta,
              total_debt: remainingAmount,
              overdue_days: overdueDays,
              observation: '',
              seller: installment.contract.seller,
              contract_files: installment.contract.contract_files,
              contracts: []
            };
          } else {
            overdueClients[clientKey].total_debt += remainingAmount;
            overdueClients[clientKey].overdue_days = Math.max(overdueClients[clientKey].overdue_days, overdueDays);
          }

          let existingContract = overdueClients[clientKey].contracts.find(c => c.id === installment.contract.id);

          if (!existingContract) {
            existingContract = {
              id: installment.contract.id,
              loan_amount: installment.contract.loan_amount,
              total_amount: installment.contract.total_amount,
              created_at: installment.contract.created_at,
              installments: []
            };
            overdueClients[clientKey].contracts.push(existingContract);
          }

          existingContract.installments.push({
            id: installment.id,
            due_date: installment.due_date,
            amount: installment.amount,
            status: installment.status,
            paid_amount: installment.paid_amount,
            remaining_amount: remainingAmount,
            days_overdue: overdueDays,
            is_overdue: true
          });
        }
      });

      const rutaGroups: { [key: number]: RutaSummary } = {};

      Object.values(overdueClients).forEach(client => {
        client.observation = observationsMap.get(client.cpf) || '';

        if (!rutaGroups[client.ruta]) {
          rutaGroups[client.ruta] = {
            ruta: client.ruta,
            total_clients: 0,
            total_debt: 0,
            clients: []
          };
        }

        rutaGroups[client.ruta].total_clients += 1;
        rutaGroups[client.ruta].total_debt += client.total_debt;
        rutaGroups[client.ruta].clients.push(client);
      });

      Object.values(rutaGroups).forEach(rutaGroup => {
        rutaGroup.clients.sort((a, b) => {
          if (b.overdue_days !== a.overdue_days) return b.overdue_days - a.overdue_days;
          return b.total_debt - a.total_debt;
        });
      });

      const summaries = Object.values(rutaGroups).sort((a, b) => a.ruta - b.ruta);
      setRutaSummaries(summaries);

    } catch (err) {
      console.error('Erro ao carregar clientes curvas:', err);
      setError('Erro ao carregar clientes em curva');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyClientData = (client: OverdueClient) => {
    const clientData = `
Dados do Cliente em Curva (90+ dias):
Nome: ${client.client_name}
CPF: ${formatCPF(client.cpf)}
Telefone: ${formatPhone(client.phone)}
Ruta: ${client.ruta}
Dias em Atraso: ${client.overdue_days}
Valor Total em Atraso: ${formatCurrency(client.total_debt)}

Telefones dos Parentes:
${client.relative_phones.map(phone => `${phone.relacao}: ${formatPhone(phone.numero)}`).join('\n')}

Endereço Residencial: ${client.address}
Endereço Comercial: ${client.business_address}
Chave PIX: ${client.pix_key}

Contratos:
${client.contracts.map(contract => `
Contrato: ${formatCurrency(contract.loan_amount)}`).join('\n')}

Vendedor: ${client.seller?.email || 'N/A'}
    `.trim();

    navigator.clipboard.writeText(clientData);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveObservation = async (clientId: string) => {
    try {
      if (!user) return;

      let clientCpf = '';
      for (const summary of rutaSummaries) {
        const client = summary.clients.find(c => c.id === clientId);
        if (client) {
          clientCpf = client.cpf;
          break;
        }
      }

      if (!clientCpf) return;

      const { data: existingObservation, error: checkError } = await supabase
        .from('client_observations')
        .select('id')
        .eq('client_id', clientCpf);

      if (checkError) throw checkError;

      if (existingObservation && existingObservation.length > 0) {
        const { error: updateError } = await supabase
          .from('client_observations')
          .update({
            observation_text: observationText,
            user_id: user.id,
            unit_id: user.unit_id
          })
          .eq('client_id', clientCpf);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('client_observations')
          .insert({
            client_id: clientCpf,
            observation_text: observationText,
            user_id: user.id,
            unit_id: user.unit_id
          });

        if (insertError) throw insertError;
      }

      setRutaSummaries(prevSummaries =>
        prevSummaries.map(summary => ({
          ...summary,
          clients: summary.clients.map(client =>
            client.id === clientId
              ? { ...client, observation: observationText }
              : client
          )
        }))
      );

      setEditingObservation(null);
      setObservationText('');
    } catch (error: any) {
      const errorMessage = error?.message || 'Erro desconhecido';
      alert(`Erro ao salvar observação: ${errorMessage}`);
    }
  };

  const startEditingObservation = (clientId: string) => {
    let currentObservation = '';
    for (const summary of rutaSummaries) {
      const client = summary.clients.find(c => c.id === clientId);
      if (client) {
        currentObservation = client.observation || '';
        break;
      }
    }

    setObservationText(currentObservation);
    setEditingObservation(clientId);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatCPF = (cpf: string) => {
    const numbers = cpf.replace(/\D/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone: string) => {
    const n = phone.replace(/\D/g, '');
    if (n.length === 11) return n.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    if (n.length === 10) return n.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    return phone;
  };

  const getFilteredSummaries = () => {
    let filtered = rutaSummaries;

    if (selectedRuta > 0) {
      filtered = filtered.filter(summary => summary.ruta === selectedRuta);
    }

    if (searchTerm) {
      filtered = filtered.map(summary => ({
        ...summary,
        clients: summary.clients.filter(client =>
          client.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.cpf.includes(searchTerm)
        )
      })).filter(summary => summary.clients.length > 0);
    }

    return filtered;
  };

  const getTotalSummary = () => {
    const filtered = getFilteredSummaries();
    return {
      total_clients: filtered.reduce((sum, summary) => sum + summary.clients.length, 0),
      total_debt: filtered.reduce((sum, summary) =>
        sum + summary.clients.reduce((clientSum, client) => clientSum + client.total_debt, 0), 0
      )
    };
  };

  // ─── Loading / Error ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3 bg-gray-50">
        <div className="w-10 h-10 border-2 border-rose-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Carregando curvas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3 bg-gray-50">
        <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center">
          <AlertTriangle className="w-6 h-6 text-red-500" />
        </div>
        <p className="text-red-600 font-medium">{error}</p>
      </div>
    );
  }

  const totalSummary = getTotalSummary();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5 pb-24">

        {/* ── HEADER ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Curvas</h1>
              <p className="text-sm text-gray-400 mt-0.5">Clientes com atraso superior a 90 dias</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2.5 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nome ou CPF..."
                  className="w-full sm:w-64 pl-9 pr-4 h-10 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent bg-gray-50 placeholder-gray-400"
                />
              </div>
              <select
                value={selectedRuta}
                onChange={(e) => setSelectedRuta(parseInt(e.target.value))}
                className="h-10 px-3 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent cursor-pointer"
              >
                <option value={0}>Todas as Rutas</option>
                <option value={1}>Ruta 1</option>
                <option value={2}>Ruta 2</option>
                <option value={3}>Ruta 3</option>
                <option value={4}>Ruta 4</option>
                <option value={5}>Ruta 5</option>
                <option value={6}>Ruta 6</option>
                <option value={7}>Ruta 7</option>
                <option value={8}>Ruta 8</option>
              </select>
            </div>
          </div>
        </div>

        {/* ── KPI CARDS ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-rose-700" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Clientes em Curva</p>
                <p className="text-2xl font-bold text-rose-700">{totalSummary.total_clients}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-6 h-6 text-rose-700" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Valor Total em Atraso</p>
                <p className="text-2xl font-bold text-rose-700">{formatCurrency(totalSummary.total_debt)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── DELETE SUCCESS BANNER ── */}
        {deleteSuccess && (
          <div className="flex items-start gap-3 p-4 rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-800">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium">{deleteSuccess}</p>
          </div>
        )}

        {/* ── SELECT-ALL TOOLBAR ── */}
        {canManageBlacklist && getFilteredSummaries().length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-3 flex items-center justify-between gap-3">
            <button
              onClick={toggleSelectAllDelete}
              className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-rose-700 transition-colors"
            >
              {allFilteredClientIds().length > 0 && allFilteredClientIds().every(id => deleteSelected.has(id)) ? (
                <CheckSquare className="w-5 h-5 text-rose-700" />
              ) : (
                <Square className="w-5 h-5 text-gray-400" />
              )}
              Selecionar todos
            </button>
            <span className="text-xs text-gray-400">
              {deleteSelected.size} selecionado{deleteSelected.size !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* ── EMPTY STATE ── */}
        {getFilteredSummaries().length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-16 flex flex-col items-center gap-3 text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            </div>
            <p className="text-gray-700 font-semibold">Nenhum cliente em curva encontrado</p>
            <p className="text-sm text-gray-400">Nenhum cliente com atraso superior a 90 dias!</p>
          </div>
        ) : (
          getFilteredSummaries().map((rutaSummary) => (
            <div key={rutaSummary.ruta} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Ruta header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50 bg-gray-50/60">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-rose-700 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">{rutaSummary.ruta}</span>
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900">Ruta {rutaSummary.ruta}</h2>
                    <p className="text-xs text-gray-400">
                      {rutaSummary.clients.length} cliente{rutaSummary.clients.length !== 1 ? 's' : ''} em curva
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-rose-700">
                    {formatCurrency(rutaSummary.clients.reduce((sum, c) => sum + c.total_debt, 0))}
                  </p>
                  <p className="text-xs text-gray-400">total em atraso</p>
                </div>
              </div>

              {/* Client cards */}
              <div className="p-4 space-y-3">
                {rutaSummary.clients.map((client) => (
                  <div
                    key={client.id}
                    className="bg-white rounded-xl border border-gray-100 border-l-4 border-l-rose-700 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="p-4">
                      {/* Top row: name + debt */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-start gap-2.5 flex-1 min-w-0">
                          {canManageBlacklist && (
                            <button
                              onClick={() => toggleDeleteSelect(client.id)}
                              title={deleteSelected.has(client.id) ? 'Desmarcar' : 'Selecionar para excluir'}
                              className="flex-shrink-0 mt-0.5"
                            >
                              {deleteSelected.has(client.id) ? (
                                <CheckSquare className="w-5 h-5 text-rose-700" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-300 hover:text-rose-500 transition-colors" />
                              )}
                            </button>
                          )}
                          <div className="flex-1 min-w-0">
                            <button
                              onClick={() => setClientDetailContractId(client.id)}
                              className="text-sm font-bold text-gray-900 hover:text-blue-600 transition-colors text-left leading-tight"
                            >
                              {client.client_name}
                            </button>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">{formatCPF(client.cpf)}</p>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-sm font-bold text-rose-700">{formatCurrency(client.total_debt)}</p>
                          <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700">
                            {client.overdue_days}d em curva
                          </span>
                        </div>
                      </div>

                      {/* Phones */}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {client.phone && (
                          <a
                            href={`https://wa.me/55${client.phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition-colors border border-emerald-100"
                          >
                            <Phone className="w-3 h-3" />
                            {formatPhone(client.phone)}
                          </a>
                        )}
                        {client.relative_phones?.filter(p => p.numero?.trim()).map((p, i) => (
                          <a
                            key={i}
                            href={`https://wa.me/55${p.numero.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition-colors border border-emerald-100"
                          >
                            <Phone className="w-3 h-3" />
                            {formatPhone(p.numero)}
                            {p.relacao && (
                              <span className="text-emerald-500 font-normal">({p.relacao})</span>
                            )}
                          </a>
                        ))}
                      </div>

                      {/* Observation */}
                      <div className="mb-3">
                        {editingObservation === client.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={observationText}
                              onChange={(e) => setObservationText(e.target.value)}
                              placeholder="Adicionar observação..."
                              className="w-full text-sm p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent bg-gray-50"
                              rows={2}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveObservation(client.id)}
                                className="h-8 flex items-center gap-1.5 px-3 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 transition-colors"
                              >
                                <Save className="w-3.5 h-3.5" />
                                Salvar
                              </button>
                              <button
                                onClick={() => { setEditingObservation(null); setObservationText(''); }}
                                className="h-8 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors"
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="flex items-start gap-2 group cursor-pointer"
                            onClick={() => startEditingObservation(client.id)}
                            title="Clique para editar observação"
                          >
                            {client.observation ? (
                              <div className="flex-1 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 flex items-start gap-2">
                                <Edit3 className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-800 leading-relaxed">{client.observation}</p>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 text-xs text-gray-400 group-hover:text-gray-600 transition-colors py-1">
                                <Edit3 className="w-3 h-3" />
                                <span className="italic">Sem observacoes — clique para adicionar</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions row */}
                      <div className="flex items-center justify-end gap-1.5 pt-2 border-t border-gray-50">
                        {user?.role === 'manager' && (
                          <button
                            onClick={() => toggleCircuitSelect(client.id)}
                            title={
                              existingCircuitStops.has(client.id)
                                ? 'Ja adicionado a uma rota'
                                : circuitSelected.has(client.id)
                                ? 'Remover da rota'
                                : 'Adicionar a rota Circuit'
                            }
                            className={`h-8 w-8 flex items-center justify-center rounded-lg transition-colors ${
                              circuitSelected.has(client.id)
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : existingCircuitStops.has(client.id)
                                ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200 border border-emerald-200'
                                : 'bg-gray-100 text-gray-500 hover:bg-blue-100 hover:text-blue-600'
                            }`}
                          >
                            {circuitSelected.has(client.id) ? (
                              <CheckSquare className="w-4 h-4" />
                            ) : existingCircuitStops.has(client.id) ? (
                              <MapPin className="w-4 h-4" />
                            ) : (
                              <Square className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleCopyClientData(client)}
                          title="Copiar dados do cliente"
                          className="h-8 w-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-rose-100 hover:text-rose-600 transition-colors"
                        >
                          {copied
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            : <Copy className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => setSelectedClient(client)}
                          title="Ver detalhes"
                          className="h-8 w-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-rose-100 hover:text-rose-600 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── CIRCUIT FLOATING BAR ── */}
      {user?.role === 'manager' && circuitSelected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 z-40 px-4 sm:px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">
                  {circuitSelected.size} cliente{circuitSelected.size !== 1 ? 's' : ''} selecionado{circuitSelected.size !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-gray-400">Prontos para criar rota no Circuit</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setCircuitSelected(new Set())}
                className="h-9 px-3 text-sm text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-xl transition-colors"
              >
                Limpar
              </button>
              <button
                onClick={handleOpenCircuitModal}
                className="h-9 px-4 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-1.5"
              >
                <MapPin className="w-4 h-4" />
                Criar Rota
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE FLOATING BAR ── */}
      {canManageBlacklist && deleteSelected.size > 0 && (
        <div
          className="fixed left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-40 px-4 sm:px-6 py-3"
          style={{ bottom: user?.role === 'manager' && circuitSelected.size > 0 ? '4.75rem' : 0 }}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-600 flex items-center justify-center flex-shrink-0">
                <ShieldBan className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {deleteSelected.size} cliente{deleteSelected.size !== 1 ? 's' : ''} selecionado{deleteSelected.size !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-gray-500">Serao excluidos e enviados para a blacklist como Curva</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setDeleteSelected(new Set())}
                className="h-9 px-3 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Limpar
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="h-9 px-4 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition-colors flex items-center gap-1.5"
              >
                <Trash2 className="w-4 h-4" />
                Excluir e enviar para blacklist
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Confirmar Exclusao</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">
                    {deleteSelected.size} cliente{deleteSelected.size !== 1 ? 's serao' : ' sera'} excluido{deleteSelected.size !== 1 ? 's' : ''} permanentemente.
                  </p>
                  <p className="text-xs text-red-700 mt-0.5 leading-relaxed">
                    Todos os contratos, parcelas e arquivos serao removidos e o CPF sera adicionado a blacklist com o motivo "Curva". Esta acao nao pode ser desfeita.
                  </p>
                </div>
              </div>
              <div className="flex gap-2.5">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="flex-1 h-11 border border-gray-200 text-gray-700 font-semibold text-sm rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteToBlacklist}
                  disabled={deleting}
                  className="flex-1 h-11 flex items-center justify-center gap-2 bg-red-600 text-white font-semibold text-sm rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Excluindo...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Excluir
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SELECTED CLIENT MODAL ── */}
      {selectedClient && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={() => setSelectedClient(null)}
        >
          <div
            className="bg-white w-full sm:rounded-2xl shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[92vh] sm:max-w-3xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 sm:px-6 py-4 flex items-center gap-3 flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-rose-700 flex items-center justify-center flex-shrink-0 shadow-sm">
                <span className="text-white text-sm font-bold">
                  {selectedClient.client_name.trim().split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wide leading-none mb-0.5">Cliente em Curva (90+ dias)</p>
                <h2 className="text-base font-bold text-gray-900 truncate">{selectedClient.client_name}</h2>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => handleCopyClientData(selectedClient)}
                  className={`h-9 flex items-center gap-1.5 px-3 text-sm font-medium rounded-xl transition-colors ${
                    copied ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span className="hidden sm:inline">{copied ? 'Copiado!' : 'Copiar'}</span>
                </button>
                <button
                  onClick={() => setSelectedClient(null)}
                  className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-4">
              {/* Client info */}
              <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Informacoes do Cliente</h3>
                </div>
                <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Nome</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedClient.client_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">CPF</p>
                    <p className="text-sm font-semibold text-gray-900 font-mono">{formatCPF(selectedClient.cpf)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Ruta</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedClient.ruta}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Telefone</p>
                    <a
                      href={`https://wa.me/55${selectedClient.phone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 hover:text-emerald-700"
                    >
                      <Phone className="w-3.5 h-3.5" />{formatPhone(selectedClient.phone)}
                    </a>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Valor em Atraso</p>
                    <p className="text-sm font-bold text-rose-700">{formatCurrency(selectedClient.total_debt)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Dias em Curva</p>
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-rose-100 text-rose-700">
                      {selectedClient.overdue_days} dias
                    </span>
                  </div>
                  {selectedClient.observation && (
                    <div className="col-span-2 sm:col-span-3">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Observacao</p>
                      <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                        <p className="text-sm text-amber-800">{selectedClient.observation}</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* Relative phones */}
              {selectedClient.relative_phones.filter(p => p.numero?.trim()).length > 0 && (
                <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contatos dos Parentes</h3>
                  </div>
                  <div className="p-4 space-y-2">
                    {selectedClient.relative_phones.filter(p => p.numero?.trim()).map((phone, index) => (
                      <div key={index} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                        <span className="text-sm text-gray-600">{phone.relacao}</span>
                        <a
                          href={`https://wa.me/55${phone.numero.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 hover:text-emerald-700"
                        >
                          <Phone className="w-3.5 h-3.5" />{formatPhone(phone.numero)}
                        </a>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Addresses */}
              <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Enderecos</h3>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Residencial</p>
                    <p className="text-sm font-medium text-gray-800 break-words leading-relaxed">
                      {selectedClient.address || <span className="italic text-gray-400">Nao informado</span>}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Comercial</p>
                    <p className="text-sm font-medium text-gray-800 break-words leading-relaxed">
                      {selectedClient.business_address || <span className="italic text-gray-400">Nao informado</span>}
                    </p>
                  </div>
                </div>
              </section>

              {/* Documents */}
              {selectedClient.contract_files.length > 0 && (
                <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Documentos</h3>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                      {selectedClient.contract_files.length}
                    </span>
                  </div>
                  <div className="p-4 space-y-2">
                    {selectedClient.contract_files.map(file => (
                      <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-4 h-4 text-gray-500" />
                          </div>
                          <span className="text-sm text-gray-700 truncate">{file.file_name}</span>
                        </div>
                        <a
                          href={file.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors flex-shrink-0"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Contracts & installments */}
              <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contratos e Parcelas em Atraso</h3>
                </div>
                <div className="p-4 space-y-4">
                  {selectedClient.contracts.map((contract) => (
                    <div key={contract.id} className="rounded-xl border border-rose-100 overflow-hidden">
                      <div className="px-4 py-3 bg-rose-50/50 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-bold text-gray-900">{formatCurrency(contract.loan_amount)}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {new Date(contract.created_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                          Curva
                        </span>
                      </div>
                      <div className="p-3 space-y-2">
                        {contract.installments.filter(i => i.is_overdue).map((installment) => (
                          <div key={installment.id} className="bg-rose-50 rounded-xl p-3 border border-rose-100">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-gray-900">{formatCurrency(installment.amount)}</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Venc. {new Date(installment.due_date + 'T00:00:00Z').toLocaleDateString('pt-BR')}
                                </p>
                                {installment.paid_amount != null && installment.paid_amount > 0 && (
                                  <p className="text-xs text-emerald-600 mt-0.5">Pago: {formatCurrency(installment.paid_amount)}</p>
                                )}
                                <p className="text-xs text-rose-700 font-semibold mt-0.5">
                                  Em atraso: {formatCurrency(installment.remaining_amount)}
                                </p>
                              </div>
                              <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold bg-rose-100 text-rose-700">
                                {installment.days_overdue}d
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {/* ── ClientDetailModal ── */}
      {clientDetailContractId && (
        <ClientDetailModal
          contractId={clientDetailContractId}
          onClose={() => setClientDetailContractId(null)}
        />
      )}

      {/* ── CircuitRouteModal ── */}
      {showCircuitModal && circuitStopsForModal.length > 0 && (
        <CircuitRouteModal
          stops={circuitStopsForModal}
          routeDate={circuitRouteDate}
          onClose={() => setShowCircuitModal(false)}
          onSuccess={() => {
            setCircuitSelected(new Set());
            setCircuitStopsForModal([]);
            fetchExistingCircuitStops();
          }}
        />
      )}
    </div>
  );
}
